"use client";

import React, { useState, useRef, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiImage, PiUpload, PiX } from "react-icons/pi";

interface CategoryItem {
    title: string;
    subtext: string;
    bgImage?: string;
}

export default function EducaeetCategoryGrid({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryTheme = content.colorPalette?.primary || "#074d3d";
    const accentYellow = content.colorPalette?.accent || "#ffbd14";
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Trigger scroll animation when section enters viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.15 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const mockImages = [
        "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1533750349088-cd871a92f312?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=600",
        "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=600"
    ];

    const default8Categories = [
        { title: "Development", subtext: "120 Courses", bgImage: mockImages[0] },
        { title: "Graphic Design", subtext: "85 Courses", bgImage: mockImages[1] },
        { title: "Business", subtext: "95 Courses", bgImage: mockImages[2] },
        { title: "Marketing", subtext: "75 Courses", bgImage: mockImages[3] },
        { title: "Photography", subtext: "60 Courses", bgImage: mockImages[4] },
        { title: "Music", subtext: "45 Courses", bgImage: mockImages[5] },
        { title: "Data Science", subtext: "110 Courses", bgImage: mockImages[6] },
        { title: "Cybersecurity", subtext: "90 Courses", bgImage: mockImages[7] }
    ];

    const rawCategories: any[] = content.categories || default8Categories;

    // Pad categories up to at least 8 items if existing state only has 6 items
    const paddedCategories = [...rawCategories];
    while (paddedCategories.length < 8) {
        const nextIdx = paddedCategories.length;
        paddedCategories.push(default8Categories[nextIdx] || default8Categories[nextIdx % 8]);
    }

    // Map categories and ensure every item has an image and proper subtext
    const categories: CategoryItem[] = paddedCategories.map((cat, idx) => ({
        ...cat,
        title: cat.title || "Category",
        subtext: cat.subtext || cat.count || "Courses available",
        bgImage: cat.bgImage || mockImages[idx % mockImages.length]
    }));



    const handleImageUpload = (index: number) => {
        setUploadingIndex(index);
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && uploadingIndex !== null) {
            const localUrl = URL.createObjectURL(file);
            const updatedCategories = [...categories];
            updatedCategories[uploadingIndex] = {
                ...updatedCategories[uploadingIndex],
                bgImage: localUrl
            };
            onContentChange?.({ ...content, categories: updatedCategories });
        }
        setUploadingIndex(null);
    };

    const handleRemoveImage = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedCategories = [...categories];
        updatedCategories[index] = {
            ...updatedCategories[index],
            bgImage: ""
        };
        onContentChange?.({ ...content, categories: updatedCategories });
    };

    return (
        <div
            ref={sectionRef}
            className={`relative py-20 md:py-28 overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ backgroundColor: "#ffffff", fontFamily: content.fontFamily || "'Inter', sans-serif" }}
        >
            {/* Scroll-Triggered Left-to-Right Animation Keyframes */}
            <style>{`
                @keyframes slideInLeftToRight {
                    0% {
                        opacity: 0;
                        transform: translateX(-50px) translateY(15px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateX(0) translateY(0);
                    }
                }
                .animate-scroll-left-to-right {
                    animation: slideInLeftToRight 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            {/* Hidden File Input for Image Uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />

            <div className="container mx-auto px-6 max-w-7xl relative z-10">
                
                {/* SECTION HEADER MATCHING WIREFRAME */}
                <div className="text-center mb-16">
                    {/* Main Text Heading */}
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
                        <EditableText
                            value={content.title || "Explore Top Courses Categories"}
                            onChange={(v) => onContentChange?.({ ...content, title: v })}
                        />
                    </h2>


                    {/* Subtext Heading Below Main Heading */}
                    <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto font-medium">
                        <EditableText
                            value={content.subtitle || "Choose from our wide range of courses and start learning today"}
                            onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
                            as="span"
                        />
                    </p>
                </div>

                {/* 8 CARDS GRID (4 COLUMNS) WITH SCROLL-TRIGGERED LEFT-TO-RIGHT ANIMATION */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {categories.map((cat, index) => {
                        const hasBgImage = Boolean(cat.bgImage);

                        return (
                            <div
                                key={index}
                                className={`group relative rounded-3xl overflow-hidden min-h-[230px] p-7 flex flex-col justify-end transition-all duration-300 border border-gray-200 shadow-sm hover:shadow-2xl hover:scale-[1.03] cursor-pointer ${
                                    isVisible ? "animate-scroll-left-to-right" : "opacity-0"
                                }`}
                                style={{
                                    backgroundColor: hasBgImage ? "transparent" : "#f8fafc",
                                    animationDelay: `${index * 0.1}s` // Left-to-right sequential delay
                                }}
                            >
                                {/* Background Image Layer */}
                                {hasBgImage && (
                                    <>
                                        <img
                                            src={cat.bgImage}
                                            alt={cat.title}
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                        />
                                        {/* Dark Gradient Overlay for Crisp Text Readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20 transition-opacity group-hover:opacity-90" />
                                    </>
                                )}

                                {/* Image Upload & Control Triggers (Visible on Hover / Edit) */}
                                <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleImageUpload(index);
                                        }}
                                        className="p-2.5 bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all text-gray-800"
                                        title="Upload / Change Background Image"
                                    >
                                        <PiUpload className="w-4 h-4" />
                                    </button>

                                    {hasBgImage && (
                                        <button
                                            onClick={(e) => handleRemoveImage(index, e)}
                                            className="p-2.5 bg-white/90 backdrop-blur-md rounded-full shadow-lg hover:bg-red-50 hover:scale-110 transition-all text-red-500"
                                            title="Remove Image"
                                        >
                                            <PiX className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>

                                {/* Content inside Card (Heading & Subtext) */}
                                <div className="relative z-10">
                                    {/* Card Heading */}
                                    <h3
                                        className={`text-xl font-bold mb-2 tracking-tight ${
                                            hasBgImage ? "text-white" : "text-gray-900 group-hover:text-emerald-700"
                                        } transition-colors`}
                                    >
                                        <EditableText
                                            value={cat.title}
                                            onChange={(v) => {
                                                const newCategories = [...categories];
                                                newCategories[index] = { ...newCategories[index], title: v };
                                                onContentChange?.({ ...content, categories: newCategories });
                                            }}
                                            as="span"
                                        />
                                    </h3>

                                    {/* Card Subtext */}
                                    <p
                                        className={`text-sm font-normal leading-relaxed ${
                                            hasBgImage ? "text-gray-200" : "text-gray-500"
                                        }`}
                                    >
                                        <EditableText
                                            value={cat.subtext}
                                            onChange={(v) => {
                                                const newCategories = [...categories];
                                                newCategories[index] = { ...newCategories[index], subtext: v };
                                                onContentChange?.({ ...content, categories: newCategories });
                                            }}
                                            as="span"
                                        />
                                    </p>
                                </div>

                                {/* Placeholder prompt if no image is present */}
                                {!hasBgImage && (
                                    <div
                                        onClick={() => handleImageUpload(index)}
                                        className="absolute top-4 left-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5 px-3 py-1.5 bg-white/90 rounded-full text-xs font-semibold text-gray-600 shadow-sm"
                                    >
                                        <PiImage className="w-3.5 h-3.5 text-emerald-600" />
                                        <span>Add BG Image</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
